import { rootApi } from '../../services/rootApi';
import { courseApi as courseApiUrl, accessApi as accessApiUrl, userApi } from '../../utils/urls';
import i18n from 'i18next';

// RTK Query API slice for Course domain (pure endpoints)
export const courseApiSlice = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // Queries
    getCourseById: builder.query({
      query: ({ courseId }) => {
        // Validate courseId before making the request
        if (!courseId || courseId === 'undefined' || courseId === 'null') {
          throw new Error(`Invalid courseId: ${courseId}`);
        }

        return {
          url: courseApiUrl(),
          method: 'POST',
          body: { method: 'findById', data: { id: courseId } },
        };
      },
      transformResponse: (response) => {
        const payload = response?.data ?? null;
        if (!payload) {
          return payload;
        }

        // Preserve existing shaping: inject Diploma page when applicable
        const { diploma, quiz } = payload?.settings || {};
        const hasDiploma = diploma?.fileId || quiz?.diplomaByQuizProgress;
        const progress = (payload?.progress || 0) * 100;
        const minProgress = diploma?.passPercentage || 80;

        if (!hasDiploma) return { ...payload };

        const diplomaPage = {
          id: 'diploma-section',
          type: 'diploma',
          progress: progress >= minProgress ? 1 : 0,
          name: i18n.t('globals.diploma'),
          pages: [
            {
              id: 'diploma',
              progress: 0,
              name:
                progress >= minProgress
                  ? i18n.t('coursePage.diploma.getYourDiploma')
                  : i18n.t('coursePage.diploma.diplomaInfo'),
            },
          ],
        };

        return { ...payload, toc: [...(payload.toc || []), diplomaPage] };
      },
      providesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),

    getCourseByUserSlug: builder.query({
      query: ({ username, slugName }) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'findByUsernameAndSlug', data: { username, slug: slugName, countStudents: true } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result) => (result?.id ? [{ type: 'Course', id: result.id }] : ['Course']),
    }),

    getCourseProgress: builder.query({
      query: ({ courseId }) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'getCourseProgress', data: { courseId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { courseId }) => [{ type: 'CourseProgress', id: courseId }],
    }),

    // Mutations
    createCourse: builder.mutation({
      query: ({ name, coverImage }) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'create', data: { name, coverImage } },
      }),
      transformResponse: (response) => response?.data ?? null,
    }),

    updateCourse: builder.mutation({
      query: ({ courseData }) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: {
          method: 'update',
          data: {
            ...courseData,
            // Normalize categories to slugs if they're objects
            categories: courseData.categories?.map((cat) =>
              typeof cat === 'string' ? cat : cat.slug
            ) || [],
          },
        },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, { courseData }) => [
        { type: 'Course', id: courseData?.id },
      ],
    }),

    deleteCourse: builder.mutation({
      query: (id) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'delete', data: { id } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, id) => [
        { type: 'Course', id },
      ],
    }),

    toggleCourseDraft: builder.mutation({
      query: ({ id }) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'toggleDraft', data: { id } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Course', id },
      ],
    }),

    duplicateCourse: builder.mutation({
      query: (id) => ({
        url: courseApiUrl(),
        method: 'POST',
        body: { method: 'duplicate', data: { id } },
      }),
      transformResponse: (response) => response?.data ?? null,
    }),

    changeAccessToCourse: builder.mutation({
      query: ({ id, expires, userId, mode }) => ({
        url: accessApiUrl(),
        method: 'POST',
        body: { method: 'course', data: { id, expires, userId, mode } },
      }),
      transformResponse: (response) => response?.data ?? null,
    }),

    getCourseUserCount: builder.query({
      query: ({ courseId }) => ({
        url: userApi(),
        method: 'POST',
        body: { method: 'countByCourseId', data: { courseId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (_, __, { courseId }) => [{ type: 'Course', id: `${courseId}-userCount` }],
    }),
  }),
});

export const {
  useGetCourseByIdQuery,
  useGetCourseByUserSlugQuery,
  useGetCourseProgressQuery,
  useGetCourseUserCountQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useToggleCourseDraftMutation,
  useDuplicateCourseMutation,
  useChangeAccessToCourseMutation,
} = courseApiSlice;

